import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormHelperText,
  Typography,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LinkIcon from "@mui/icons-material/Link";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteIcon from "@mui/icons-material/Delete";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { v4 as uuidv4 } from "uuid";
import { ClientUploadDeliveryAddressPhoto, ClientDeleteDeliveryAddressPhoto } from "api/client";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const MapClickHandler = ({ onLocationSelect }) => {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

const DraggableMarker = ({ position, onPositionChange }) => {
  const markerRef = React.useRef(null);
  return (
    <Marker
      draggable
      eventHandlers={{
        dragend() {
          const pos = markerRef.current?.getLatLng();
          if (pos) onPositionChange(pos.lat, pos.lng);
        },
      }}
      position={position}
      ref={markerRef}
    />
  );
};

const isValidGoogleMapURL = (url) =>
  /^(https?:\/\/)?(www\.)?(google\.com|maps\.google\.com|goo\.gl|maps\.app\.goo\.gl)\/([a-zA-Z0-9@:%_+.~#?&//=,-]*)$/.test(url);

const parseGoogleMapsURL = (url) => {
  const patterns = [
    /@(-?\d+\.?\d*),(-?\d+\.?\d*)/,
    /q=(-?\d+\.?\d*),(-?\d+\.?\d*)/,
    /!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
  }
  return null;
};

const DEFAULT_LAT = 17.9757;
const DEFAULT_LNG = 102.6331;
const ADDRESS_TYPES = ["Home", "Office", "Apartment", "Shop", "Other"];
const MAX_PHOTOS = 5;

const buildInitialState = (initialData) => {
  // For older records that have coordinates embedded in the map URL but no lat/lng field,
  // try to parse them so the map marker is positioned correctly on edit.
  let lat = initialData?.lat || null;
  let lng = initialData?.lng || null;
  if ((!lat || !lng) && initialData?.map) {
    const coords = parseGoogleMapsURL(initialData.map);
    if (coords) { lat = coords.lat; lng = coords.lng; }
  }
  return {
    id: initialData?.id || uuidv4(),
    map: initialData?.map || "",
    title: initialData?.title || "",
    description: initialData?.description || "",
    isDefault: initialData?.isDefault ?? true,
    lat: lat || DEFAULT_LAT,
    lng: lng || DEFAULT_LNG,
    contactName: initialData?.contactName || "",
    contactPhone: initialData?.contactPhone || "",
    floor: initialData?.floor || "",
    unitNumber: initialData?.unitNumber || "",
    notes: initialData?.notes || "",
  };
};

const DeliveryAddressDialog = ({ open, onClose, onSave, initialData }) => {
  const isEdit = Boolean(initialData);
  const photoInputRef = useRef(null);

  const [address, setAddress] = useState(() => buildInitialState(initialData));
  // photos: [{ preview, file, filename }]
  // file = null → existing photo already on server
  // file = File → newly selected, not yet uploaded
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [previewIndex, setPreviewIndex] = useState(null);

  // Sync form when dialog opens with (possibly new) initialData
  useEffect(() => {
    if (open) {
      setAddress(buildInitialState(initialData));
      setPhotos(
        (initialData?.photos || []).map((f) => ({
          preview: `/uploads/delivery-photos/${f}`,
          file: null,
          filename: f,
        }))
      );
      setErrors({});
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setAddress((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleLocationSelect = (lat, lng) => {
    setAddress((prev) => ({
      ...prev,
      lat,
      lng,
      map: `https://maps.google.com/?q=${lat},${lng}`,
    }));
    if (errors.map) setErrors((prev) => ({ ...prev, map: "" }));
  };

  const handleMapURLChange = (e) => {
    const value = e.target.value;
    setAddress((prev) => ({ ...prev, map: value }));
    if (errors.map) setErrors((prev) => ({ ...prev, map: "" }));
    if (value && isValidGoogleMapURL(value)) {
      const coords = parseGoogleMapsURL(value);
      if (coords) setAddress((prev) => ({ ...prev, ...coords }));
    }
  };

  // Photo handlers
  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const slots = MAX_PHOTOS - photos.length;
    files.slice(0, slots).forEach((file) => {
      setPhotos((prev) => [
        ...prev,
        { preview: URL.createObjectURL(file), file, filename: null },
      ]);
    });
    e.target.value = "";
  };

  const handleRemovePhoto = (index) => {
    setPhotos((prev) => {
      const updated = [...prev];
      const removed = updated.splice(index, 1)[0];
      // Revoke local blob URLs to free memory
      if (removed.preview.startsWith("blob:")) URL.revokeObjectURL(removed.preview);
      // If it was an existing server photo, delete it
      if (removed.filename && !removed.file) {
        ClientDeleteDeliveryAddressPhoto(removed.filename);
      }
      return updated;
    });
  };

  const validate = () => {
    const e = {};
    if (!address.map || !address.lat || !address.lng) e.map = "Please set a location on the map or paste a Google Maps URL.";
    if (!address.title) e.title = "Please select an address type.";
    if (!address.description.trim()) e.description = "Please enter a description.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      // Upload any newly selected photos
      const finalPhotos = photos
        .filter((p) => p.filename && !p.file) // existing
        .map((p) => p.filename);

      for (const photo of photos.filter((p) => p.file)) {
        const res = await ClientUploadDeliveryAddressPhoto(photo.file);
        if (res?.success) finalPhotos.push(res.data.filename);
      }

      onSave({ ...address, photos: finalPhotos });
      onClose();
    } catch {
      // upload error already logged
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      onClose={onClose}
      sx={{
        "& .MuiDialog-paper": {
          width: { xs: "100%", sm: "90%", md: "80%" },
          maxWidth: { xs: "100%", sm: "600px", md: "900px" },
          margin: { xs: 0, sm: 2 },
          height: { xs: "100vh", sm: "auto" },
          maxHeight: { xs: "100vh", sm: "90vh" },
          borderRadius: { xs: 0, sm: 2 },
        },
      }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        {isEdit ? "Edit Delivery Address" : "Add a New Delivery Address"}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "grey.500" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ overflowY: "auto" }}>
        <Grid container spacing={2}>

          {/* ── Map URL ── */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
              <LinkIcon fontSize="small" /> Google Maps URL
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Google Maps URL"
              name="map"
              value={address.map}
              onChange={handleMapURLChange}
              placeholder="https://maps.google.com/?q=17.9757,102.6331"
              error={!!errors.map || (address.map !== "" && !isValidGoogleMapURL(address.map))}
              helperText={
                errors.map ||
                (address.map !== "" && !isValidGoogleMapURL(address.map)
                  ? "Invalid Google Maps URL"
                  : "Paste URL to auto-set coordinates")
              }
            />
          </Grid>

          {/* ── Interactive Map ── */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
              <LocationOnIcon fontSize="small" /> Pin Location (click or drag marker)
            </Typography>
            <Box
              sx={{
                width: "100%",
                height: { xs: 220, sm: 280 },
                borderRadius: 2,
                overflow: "hidden",
                border: "1px solid",
                borderColor: errors.map ? "error.main" : "divider",
              }}
            >
              <MapContainer
                center={[address.lat, address.lng]}
                zoom={15}
                style={{ height: "100%", width: "100%" }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />
                <DraggableMarker
                  position={[address.lat, address.lng]}
                  onPositionChange={handleLocationSelect}
                />
                <MapClickHandler onLocationSelect={handleLocationSelect} />
              </MapContainer>
            </Box>
            {address.lat && address.lng && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                📍 {address.lat.toFixed(6)}, {address.lng.toFixed(6)}
              </Typography>
            )}
          </Grid>

          {/* ── Address Type ── */}
          <Grid item xs={12}>
            <FormControl error={!!errors.title}>
              <FormLabel sx={{ fontSize: "0.85rem", fontWeight: 600 }}>Address Type *</FormLabel>
              <RadioGroup name="title" value={address.title} onChange={handleChange} row>
                {ADDRESS_TYPES.map((opt) => (
                  <FormControlLabel
                    key={opt}
                    value={opt}
                    control={<Radio size="small" color="secondary" />}
                    label={opt}
                  />
                ))}
              </RadioGroup>
              {errors.title && <FormHelperText>{errors.title}</FormHelperText>}
            </FormControl>
          </Grid>

          {/* ── Description ── */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Address Description *"
              name="description"
              value={address.description}
              onChange={handleChange}
              multiline
              rows={2}
              error={!!errors.description}
              helperText={errors.description || "Street address or nearby landmark"}
            />
          </Grid>

          {/* ── Floor & Unit ── */}
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Floor / Level"
              name="floor"
              value={address.floor}
              onChange={handleChange}
              placeholder="e.g. 3, Ground"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Unit / Room No."
              name="unitNumber"
              value={address.unitNumber}
              onChange={handleChange}
              placeholder="e.g. 4B, Room 101"
            />
          </Grid>

          {/* ── Contact ── */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Recipient Name"
              name="contactName"
              value={address.contactName}
              onChange={handleChange}
              placeholder="Name of the person to receive"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Recipient Phone"
              name="contactPhone"
              value={address.contactPhone}
              onChange={handleChange}
              placeholder="e.g. +8562012345678"
            />
          </Grid>

          {/* ── Notes ── */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Delivery Notes"
              name="notes"
              value={address.notes}
              onChange={handleChange}
              multiline
              rows={2}
              placeholder="e.g. Ring the bell, gate code is 1234, leave at reception..."
            />
          </Grid>

          {/* ── Photos (max 5) ── */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight={600} mb={1}>
              Location Photos ({photos.length}/{MAX_PHOTOS})
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {photos.map((photo, i) => (
                <Box
                  key={i}
                  sx={{
                    position: "relative",
                    width: 80,
                    height: 80,
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                    flexShrink: 0,
                    cursor: "pointer",
                    "&:hover .zoom-hint": { opacity: 1 },
                  }}
                  onClick={() => setPreviewIndex(i)}
                >
                  <img
                    src={photo.preview}
                    alt={`photo-${i}`}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                  {/* Zoom hint overlay */}
                  <Box
                    className="zoom-hint"
                    sx={{
                      position: "absolute",
                      inset: 0,
                      bgcolor: "rgba(0,0,0,0.3)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      opacity: 0,
                      transition: "opacity 0.2s",
                    }}
                  >
                    <ZoomInIcon sx={{ color: "white", fontSize: 28 }} />
                  </Box>
                </Box>
              ))}

              {photos.length < MAX_PHOTOS && (
                <Box
                  onClick={() => photoInputRef.current?.click()}
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: 2,
                    border: "2px dashed",
                    borderColor: "divider",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "text.disabled",
                    flexShrink: 0,
                    transition: "border-color 0.2s, color 0.2s",
                    "&:hover": { borderColor: "secondary.main", color: "secondary.main" },
                  }}
                >
                  <AddPhotoAlternateIcon fontSize="small" />
                  <Typography variant="caption" fontSize="0.6rem">Add Photo</Typography>
                </Box>
              )}
            </Box>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={handlePhotoSelect}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
              Click a photo to preview · Upload photos of the entrance, building, or landmarks (max {MAX_PHOTOS}, 5 MB each)
            </Typography>
          </Grid>

          {/* ── Default switch ── */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={address.isDefault}
                  onChange={handleChange}
                  name="isDefault"
                  color="secondary"
                />
              }
              label="Set as default delivery address"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" color="secondary" sx={{ borderRadius: 2 }} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="secondary"
          variant="contained"
          disabled={saving}
          sx={{ borderRadius: 2, minWidth: 120 }}
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : isEdit ? "Save Changes" : "Add Address"}
        </Button>
      </DialogActions>

      {/* ── Photo Lightbox ── */}
      {previewIndex !== null && photos[previewIndex] && (
        <Dialog
          open
          onClose={() => setPreviewIndex(null)}
          maxWidth="md"
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              bgcolor: "#000",
              borderRadius: 2,
              overflow: "hidden",
              m: { xs: 1, sm: 2 },
            },
          }}
        >
          {/* Close */}
          <IconButton
            onClick={() => setPreviewIndex(null)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 10,
              bgcolor: "rgba(0,0,0,0.5)",
              color: "white",
              "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Image */}
          <Box sx={{ position: "relative", width: "100%", minHeight: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <img
              src={photos[previewIndex].preview}
              alt="preview"
              style={{ maxWidth: "100%", maxHeight: "75vh", objectFit: "contain", display: "block" }}
            />

            {/* Prev arrow */}
            {photos.length > 1 && (
              <IconButton
                onClick={() => setPreviewIndex((previewIndex - 1 + photos.length) % photos.length)}
                sx={{
                  position: "absolute",
                  left: 8,
                  bgcolor: "rgba(0,0,0,0.45)",
                  color: "white",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                }}
              >
                <ArrowBackIosNewIcon fontSize="small" />
              </IconButton>
            )}

            {/* Next arrow */}
            {photos.length > 1 && (
              <IconButton
                onClick={() => setPreviewIndex((previewIndex + 1) % photos.length)}
                sx={{
                  position: "absolute",
                  right: 8,
                  bgcolor: "rgba(0,0,0,0.45)",
                  color: "white",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                }}
              >
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          {/* Bottom bar: counter + delete */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1,
              bgcolor: "rgba(0,0,0,0.7)",
            }}
          >
            <Typography variant="caption" color="grey.400">
              {previewIndex + 1} / {photos.length}
            </Typography>
            <Tooltip title="Delete this photo">
              <IconButton
                size="small"
                onClick={() => {
                  handleRemovePhoto(previewIndex);
                  setPreviewIndex(photos.length > 1 ? Math.min(previewIndex, photos.length - 2) : null);
                }}
                sx={{
                  bgcolor: "rgba(211,47,47,0.15)",
                  color: "error.light",
                  border: "1px solid",
                  borderColor: "error.dark",
                  "&:hover": { bgcolor: "rgba(211,47,47,0.4)" },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Dialog>
      )}
    </Dialog>
  );
};

export default DeliveryAddressDialog;
